<script lang="ts">
  import { ref, onMounted, nextTick, watch } from "vue";
  import { DatePicker } from "primevue";
  import Message from "@/ui/message/message.vue";
  import { useField } from "vee-validate";

  DatePicker.compatConfig = {
    MODE: 3,
    COMPONENT_V_MODEL: false,
  };

  export default {
    components: { DatePicker, Message },
    props: {
      name: {
        type: String,
        default: "",
      },
      label: {
        type: String,
        default: "",
      },
      required: {
        type: Boolean,
        default: false,
      },
      maxDate: {
        type: Date,
        default: null,
      },
      minDate: {
        type: Date,
        default: null,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      dateFormat: {
        type: String,
        default: "dd-mm-yy",
      },
      timeOnly: {
        type: Boolean,
        default: false,
      },
    },
    setup(props) {
      const { value: localValue, errorMessage, meta, handleBlur: fieldBlur, setValue } = useField<Date>(() => props.name);

      const datepickerRef = ref();

      // Watch for value changes and log them
      watch(localValue, (newValue, oldValue) => {
        // if time is 00:00, set it to 12:00
        if (!props.timeOnly && newValue && newValue.getHours() === 0 && newValue.getMinutes() === 0) {
          setValue(new Date(newValue.setHours(12, 0, 0, 0)));
        }
      });

      // Attach a manual input listener to the real input
      onMounted(() => {
        nextTick(() => {
          const input = datepickerRef.value?.$el?.querySelector("input");
          if (input) {
            input.addEventListener("input", (event: Event) => {
              const target = event.target as HTMLInputElement;
              let raw = target.value.replace(/\D/g, ""); // only digits
              if (raw.length === 8) {
                const dd = raw.slice(0, 2);
                const mm = raw.slice(2, 4);
                const yy = raw.slice(4, 8);
                const date = new Date(`${yy}-${mm}-${dd}`);
                if (!isNaN(date.getTime())) {
                  setValue(date);
                }
              }
            });
          }
        });
      });

      return {
        localValue,
        errorMessage,
        meta,
        fieldBlur,
        setValue,
        datepickerRef,
        name: props.name,
      };
    },
  };
</script>

<template>
  <div class="flex flex-col gap-[4px]">
    <label v-if="label" :for="name" class="text-[14px] font-normal">
      {{ label }}
      <span v-if="required" class="text-[#FF55C6]">*</span>
    </label>
    <DatePicker
      ref="datepickerRef"
      v-bind="{ ...$attrs, ...$props }"
      v-model="localValue"
      :inputId="name"
      :invalid="!!errorMessage"
      class="h-[40px]"
      :date-format="timeOnly ? 'HH:mm' : dateFormat"
      :time-only="timeOnly"
    />
    <Message v-if="errorMessage" severity="error" size="small" variant="simple" :text="errorMessage" />
  </div>
</template>
